import type { Plurals, Strings } from '../index';

// Neutral infinitive wording is used where possible ("Neue Wallet erstellen"), which reads
// naturally in a menu and avoids choosing between "Sie" and "du".
export const strings: Strings = {
  'menu.home': 'Start',
  'menu.notes': 'Notizen',
  'menu.reminders': 'Erinnerungen',
  'menu.passwords': 'Passwörter',
  'menu.cards': 'Karten',
  'menu.photoIds': 'Ausweise',
  'menu.collections': 'Sammlungen',
  'menu.conflicts': 'Konflikte ({count})',
  'menu.settings': 'Einstellungen',
  'menu.lock': 'Wallet sperren',
  'menu.language': 'Sprache',

  'type.note': 'Notiz',
  'type.note.plural': 'Notizen',
  'type.password': 'Passwort',
  'type.password.plural': 'Passwörter',
  'type.card': 'Karte',
  'type.card.plural': 'Karten',
  'type.photoId': 'Ausweis',
  'type.photoId.plural': 'Ausweise',

  'section.upcoming': 'Anstehende Erinnerungen',
  'section.pinned': 'Angeheftet',
  'section.recent': 'Zuletzt',
  'section.searchResults': 'Suchergebnisse',
  'section.conflicts': 'Konflikte',

  'action.addNew': '+ Neu',
  'action.back': 'Zurück',
  'search.placeholder': 'Suchen oder neu anlegen',

  'gate.title': 'Alles Wichtige',
  'gate.copy': 'Ihre Wallet wird auf diesem Gerät verschlüsselt, bevor irgendetwas synchronisiert wird.',
  'gate.create': 'Neue Wallet erstellen',
  'gate.open': 'Vorhandene Wallet öffnen',
  'gate.recover': 'Zugang wiederherstellen',

  'empty.addFirst': 'Ersten Eintrag hinzufügen.',
  'empty.nothingHere': 'Hier ist noch nichts.',
  'empty.noResults': 'Keine gespeicherten Einträge passen zu dieser Suche.',

  'action.cancel': 'Abbrechen',
  'action.save': 'Speichern',
  'action.saving': 'Wird gespeichert…',
  'action.close': 'Schließen',
  'action.edit': 'Bearbeiten',
  'action.delete': 'Löschen',
  'action.remove': 'Entfernen',
  'action.change': 'Ändern',
  'action.clear': 'Leeren',
  'action.add': 'Hinzufügen',
  'action.show': 'Anzeigen',
  'action.hide': 'Verbergen',
  'action.more': 'Mehr',

  'field.title': 'Titel',
  'field.body': 'Text',
  'field.username': 'Benutzername',
  'field.password': 'Passwort',
  'field.website': 'Website',
  'field.notes': 'Notizen',
  'field.cardNumber': 'Kartennummer',
  'field.documentNumber': 'Dokumentnummer',
  'field.expiry': 'Gültig bis',
  'field.securityCode': 'Sicherheitscode',
  'field.cvv': 'CVV / CVC',
  'field.pin': 'PIN',
  'field.documentType': 'Dokumentart',
  'field.reminder': 'Erinnerung',
  'field.collections': 'Sammlungen',
  'field.idPhotos': 'Ausweisfotos',
  'field.attachments': 'Anhänge',

  'editor.newItem': 'Neu: {type}',
  'editor.editItem': '{type} bearbeiten',
  'editor.titlePlaceholder': 'Titel für {type}',
  'editor.selectDocumentType': 'Dokumentart wählen',
  'editor.noneSelected': 'Nichts ausgewählt',
  'editor.optional': 'Optional',
  'editor.optionalFields': 'Optionale Felder',
  'editor.reminderDate': 'Datum der Erinnerung',
  'editor.optionalTime': 'Uhrzeit (optional)',
  'editor.shortcutHint': 'Strg+Enter speichert. Esc bricht ab.',
  'editor.addTitle': 'Bitte einen Titel eingeben.',
  'editor.saveFailed': 'Dieser Eintrag konnte nicht gespeichert werden.',
  'editor.photoFailed': 'Dieses Foto konnte nicht vorbereitet werden.',
  'editor.attachmentFailed': 'Dieser Anhang konnte nicht vorbereitet werden.',
  'editor.photoNote': 'Fotos bis {mb} MB werden auf diesem Gerät verschlüsselt gespeichert und automatisch synchronisiert, wenn die Synchronisierung aktiv ist.',
  'editor.attachmentNote': 'Dateien werden genau so verschlüsselt, wie sie ausgewählt wurden, und auf diesem Gerät gespeichert. Safe Wallet blockiert gängige ausführbare Dateien, Skripte, aktive Webinhalte und Dateien mit Makros, prüft Dateien aber nicht auf Schadsoftware.',
  'editor.attachmentButton': 'Anhang (max. {mb} MB)',
  'editor.addingAttachment': 'Anhang wird hinzugefügt…',
  'editor.preparing': 'Wird vorbereitet…',
  'editor.takePhoto': 'Foto aufnehmen',
  'editor.choosePhoto': 'Foto auswählen',
  'editor.front': 'Vorderseite',
  'editor.back': 'Rückseite',
  'editor.tapAddFront': 'Tippen, um die Vorderseite hinzuzufügen',
  'editor.tapAddBack': 'Tippen, um die Rückseite hinzuzufügen',
  'editor.photoNotOnDevice': 'Das verschlüsselte Foto ist nicht auf diesem Gerät gespeichert.',
  'editor.pinItem': 'Anheften',
  'editor.pinned': 'Angeheftet',
  'editor.deleteItem': 'Eintrag löschen',

  'viewer.noBody': 'Kein Text vorhanden.',
  'viewer.notSet': 'Nicht gesetzt',
  'viewer.escCloses': 'Esc schließt diese Ansicht.',

  'attach.title': 'Anhang',
  'attach.notOnDevice': 'Der verschlüsselte Anhang ist noch nicht auf diesem Gerät gespeichert.',
  'attach.photoNotOnDevice': 'Das verschlüsselte Foto ist noch nicht auf diesem Gerät gespeichert.',
  'attach.openingPhoto': 'Verschlüsseltes Foto wird geöffnet…',
  'attach.opening': 'Wird geöffnet…',
  'attach.notStored': 'Nicht auf diesem Gerät gespeichert',
  'attach.couldNotOpen': 'Dieser Anhang konnte nicht geöffnet werden.',
  'attach.previewUnavailable': 'Für diesen Dateityp ist keine Vorschau verfügbar. Laden Sie die Datei herunter und öffnen Sie sie mit einer App, der Sie vertrauen.',
  'attach.previewLimited': 'Vorschau auf das erste 1 MB begrenzt.',
  'attach.openPdf': 'PDF öffnen',
  'attach.download': 'Herunterladen',

  'expiry.label': 'Gültig bis (MM/JJ)',
  'expiry.month': 'Gültigkeitsmonat',
  'expiry.chooseMonthYear': 'Monat und Jahr wählen',
  'expiry.chooseDate': 'Gültigkeitsdatum wählen',
  'expiry.previousYear': 'Vorheriges Jahr',
  'expiry.nextYear': 'Nächstes Jahr',

  'collections.createFirst': 'Legen Sie zuerst Sammlungen im Hauptmenü an.',
  'chooser.addNew': 'Neu hinzufügen',

  'settings.title': 'Einstellungen',
  'settings.account': 'Konto',
  'settings.security': 'Sicherheit',
  'settings.recovery': 'Wiederherstellung',
  'settings.thisDevice': 'Dieses Gerät',
  'settings.appearance': 'Darstellung',
  'settings.searchPrivacy': 'Datenschutz bei der Suche',
  'settings.dangerZone': 'Kritischer Bereich',
  'settings.notSignedIn': 'Nicht angemeldet',
  'settings.pinOnly': 'Nur PIN',
  'settings.pinAndFingerprint': 'PIN + Fingerabdruck',
  'settings.changePinRow': 'Die App-PIN für dieses Gerät ändern. Die aktuelle PIN wird benötigt.',
  'settings.lockNow': 'Jetzt sperren',
  'settings.chooseAnotherWallet': 'Andere Wallet wählen',
  'settings.useAnotherAccount': 'Anderes Konto verwenden',
  'settings.categoryLabels': 'Kategoriebezeichnungen',
  'settings.rowSpacing': 'Zeilenabstand',
  'settings.textSize': 'Schriftgröße',
  'settings.autoLock': 'Automatisch sperren',
  'settings.optionName': 'Name',
  'settings.optionIcon': 'Symbol',
  'settings.optionBoth': 'Beides',
  'settings.optionLarge': 'Groß',
  'settings.optionMedium': 'Mittel',
  'settings.optionCompact': 'Kompakt',
  'settings.optionSmall': 'Klein',
  'settings.searchPrivacyNote': 'Der entschlüsselte Suchindex besteht nur auf diesem entsperrten Gerät und wird gelöscht, sobald die Wallet gesperrt wird. Passwörter, PINs, Karten- und Kontonummern, Sicherheitscodes, Ausweisnummern, Wiederherstellungscodes und ähnliche Geheimnisse werden nie indexiert.',
  'settings.exampleProjection': 'Beispiel für den ersten Eintrag',
  'settings.noItemsYet': 'Noch keine Einträge.',
  'settings.removeWalletNote': 'Wenn Sie diese Wallet aus diesem Browser entfernen, wird die verschlüsselte Sicherung im Konto nicht gelöscht.',
  'settings.removeWallet': 'Diese lokale Wallet entfernen',

  'pin.changeTitle': 'App-PIN ändern',
  'pin.change': 'PIN ändern',
  'pin.changing': 'Wird geändert…',
  'pin.current': 'Aktuelle PIN',
  'pin.new': 'Neue PIN',
  'pin.repeat': 'Neue PIN wiederholen',
  'pin.accountPassword': 'Kontopasswort',
  'pin.accountPasswordHint': 'Wird benötigt, um die Anmeldung auf Ihren anderen Geräten zu aktualisieren.',
  'pin.noteSynced': 'Ihre gespeicherten Einträge müssen nicht neu verschlüsselt werden. Da diese Wallet synchronisiert wird, wird Ihr Kontopasswort benötigt, damit die neue PIN auch bei der Anmeldung auf einem neuen Gerät funktioniert.',
  'pin.noteLocal': 'Ihre aktuelle PIN entsperrt diese Wallet auf diesem Gerät. Ihre gespeicherten Einträge müssen nicht neu verschlüsselt werden.',
  'pin.errCurrent': 'Geben Sie Ihre aktuelle App-PIN mit 6 bis 12 Ziffern ein.',
  'pin.errNew': 'Verwenden Sie eine neue App-PIN mit 6 bis 12 Ziffern.',
  'pin.errMismatch': 'Die beiden neuen PIN-Eingaben stimmen nicht überein.',
  'pin.errSame': 'Wählen Sie eine neue PIN, die sich von der aktuellen unterscheidet.',
  'pin.errPassword': 'Geben Sie Ihr Kontopasswort ein.',
  'pin.errFailed': 'Die App-PIN konnte nicht geändert werden.',

  'fingerprint.title': 'Entsperren per Fingerabdruck',
  'fingerprint.settingUp': 'Wird eingerichtet…',
  'fingerprint.unavailable': 'Dieses Gerät kann das nicht.',
  'fingerprint.errOn': 'Das konnte nicht aktiviert werden.',
  'fingerprint.errOff': 'Das konnte nicht deaktiviert werden.',

  'sync.off': 'Aus — nur auf diesem Gerät',
  'sync.notConfigured': 'Aus — Synchronisierung nicht eingerichtet',
  'sync.connecting': 'Verbindung wird hergestellt…',
  'sync.uploading': 'Ein — verschlüsselte Sicherung wird synchronisiert…',
  'sync.waiting': 'Ein — verschlüsselte Änderungen warten auf den Upload',
  'sync.connected': 'Ein — verschlüsselte Sicherung verbunden',
  'sync.offline': 'Offline — verschlüsselte Sicherung pausiert',

  'account.signedIn': 'Angemeldet',
  'account.signedInAs': 'Angemeldet als',
  'account.anotherAccount': 'Anderes Konto',
  'account.currentAccount': 'Aktuelles Konto',
  'account.wallet': 'Wallet',
  'account.sync': 'Synchronisierung',
  'account.device': 'Gerät',
  'account.signOut': 'Abmelden',
  'account.signOutOf': 'Von {account} abmelden',
  'account.email': 'E-Mail',
  'account.pinLabel': 'Safe-Wallet-PIN',
  'account.pinHint': 'Ihre PIN für diese Wallet. Wenn dieses Konto bereits eine Wallet hat, verwenden Sie deren PIN.',
  'account.pinPlaceholder': '6–12 Ziffern',
  'account.signInTab': 'Anmelden',
  'account.createTab': 'Konto erstellen',
  'account.forgotPassword': 'Kontopasswort vergessen?',
  'account.signInSync': 'Anmelden & synchronisieren',
  'account.createSync': 'Konto erstellen & synchronisieren',
  'account.signInSyncTitle': 'Anmelden & Synchronisieren',
  'account.differentSession': 'In diesem Browser ist ein Konto angemeldet, aber diese lokale Wallet ist nicht mit diesem Konto verbunden. Öffnen Sie unten „Anmelden & Synchronisieren“, um das Konto zu verbinden, dem diese Wallet gehört, oder melden Sie sich zuerst ab, um ein anderes Konto zu verwenden.',
  'account.notConfigured': 'Die Synchronisierung ist für diese Installation nicht eingerichtet.',
  'account.confirmEmail': 'Bestätigen Sie das Konto über die E-Mail und melden Sie sich dann hier an. Diese lokale Wallet ist noch nicht verbunden.',
  'account.errPin': 'Geben Sie die Safe-Wallet-PIN mit 6 bis 12 Ziffern für diese Wallet ein.',
  'account.errSession': 'Die Kontositzung konnte nicht hergestellt werden.',
  'account.connected': 'Wallet verbunden. Die Synchronisierung startet.',
  'account.connectedNoSync': 'Konto verbunden. Die Synchronisierung ist in dieser Umgebung nicht verfügbar.',
  'account.errConnect': 'Dieses Konto konnte nicht verbunden werden.',
  'account.resetSent': 'Prüfen Sie Ihre E-Mails auf den Link zum Zurücksetzen des Passworts.',
  'account.errReset': 'Die E-Mail zum Zurücksetzen konnte nicht gesendet werden.',
  'account.signedOut': 'Vom Konto abgemeldet. Die verschlüsselte Wallet bleibt auf diesem Gerät verfügbar.',

  'pairing.advanced': 'Erweitert: anderes Gerät freigeben',
  'pairing.advancedNote': 'Verwenden Sie dies nur, wenn die normale Anmeldung mit Passwort und PIN nicht möglich ist. Vergleichen Sie vor der Freigabe den Fingerabdruck auf beiden Geräten.',
  'pairing.none': 'Keine ausstehenden Geräte.',
  'pairing.invalidKey': 'Ungültiger Kopplungsschlüssel',
  'pairing.calculating': 'Fingerabdruck wird berechnet…',
  'pairing.review': 'Prüfen',
  'pairing.reviewTitle': 'Neues Gerät prüfen',
  'pairing.fingerprint': 'Prüf-Fingerabdruck',
  'pairing.compare': 'Vergleichen Sie jede Gruppe mit dem Fingerabdruck auf dem neuen Gerät.',
  'pairing.doNotApprove': 'Bei Abweichungen nicht freigeben.',
  'pairing.mismatchNote': 'Das kann bedeuten, dass der Kopplungsschlüssel geändert wurde und der Wallet-Schlüssel an ein falsches Gerät gesendet werden könnte.',
  'pairing.confirm': 'Ich habe beide Fingerabdrücke verglichen und sie stimmen genau überein.',
  'pairing.approve': 'Gerät freigeben',
  'pairing.approving': 'Wird freigegeben…',
  'pairing.approved': '{device} freigegeben.',
  'pairing.errInvalid': 'Diese Freigabeanfrage enthält einen ungültigen Kopplungsschlüssel. Lehnen Sie sie ab und starten Sie eine neue Anfrage.',
  'pairing.errChanged': 'Der Kopplungsschlüssel hat sich während der Prüfung geändert. Brechen Sie diese Anfrage ab.',
  'pairing.errApprove': 'Dieses Gerät konnte nicht freigegeben werden.',

  'wallet.localOnly': 'Nur lokale Wallet',
  'wallet.lastUsedRecently': 'Zuletzt kürzlich verwendet',
  'wallet.notConnected': 'Mit keinem Konto verbunden',
  'wallet.availableOnDevice': 'Wallet auf diesem Gerät verfügbar',
  'wallet.onlyThisDevice': 'Nur auf diesem Gerät — Synchronisierung ist aus',

  'fingerprint.confirmBackup': 'Entsperren per Fingerabdruck aktivieren?\n\nDanach benötigen Sie zum Öffnen dieser Wallet auf diesem Gerät sowohl Ihren Fingerabdruck als auch Ihre PIN. Falls der Fingerabdruck einmal nicht mehr funktioniert, kommen Sie über Ihre Sicherungsanmeldung oder einen vertrauenswürdigen Kontakt wieder hinein.',
  'fingerprint.confirmNoBackup': 'Entsperren per Fingerabdruck aktivieren?\n\nDanach benötigen Sie zum Öffnen dieser Wallet auf diesem Gerät sowohl Ihren Fingerabdruck als auch Ihre PIN. Diese Wallet hat noch keine Sicherung. Wenn der Fingerabdruck einmal nicht mehr funktioniert, kann sie nicht wieder geöffnet werden. Es wird dringend empfohlen, zuerst die Synchronisierung einzuschalten.',

  'gate.cloudNotConfigured': 'Die Cloud ist noch nicht eingerichtet. Sie können jetzt eine lokale Wallet erstellen und sie später in den Einstellungen verbinden.',

  'create.title': 'App-PIN erstellen',
  'create.intro': 'Die PIN entsperrt dieses Gerät. Ihr Kontopasswort ist davon getrennt, sobald Sie die Synchronisierung einschalten.',
  'create.walletName': 'Name der Wallet',
  'create.walletNameHint': 'Wird in diesem Browser vor der PIN-Eingabe angezeigt.',
  'create.walletNamePlaceholder': 'Alex, Sam, Familientresor…',
  'create.deviceName': 'Gerätename',
  'create.appPin': 'App-PIN',
  'create.repeatPin': 'PIN wiederholen',
  'create.creating': 'Wird erstellt…',
  'create.createWallet': 'Wallet erstellen',
  'create.errPin': 'Verwenden Sie eine App-PIN mit 6 bis 12 Ziffern.',
  'create.errMismatch': 'Die beiden PIN-Eingaben stimmen nicht überein.',
  'create.errFailed': 'Diese Wallet konnte nicht erstellt werden.',
  'create.errHello': 'Das Entsperren per Fingerabdruck konnte nicht aktiviert werden.',
  'create.fingerprintTitle': 'Fingerabdruck oder Gesicht hinzufügen?',
  'create.fingerprintCopy': 'Ein zweites Schloss, damit eine gestohlene Kopie Ihrer Wallet nicht geknackt werden kann. Sie geben weiterhin Ihre PIN ein.',
  'create.turnOn': 'Aktivieren',
  'create.notNow': 'Jetzt nicht',

  'unlock.title': 'Safe Wallet entsperren',
  'unlock.unlock': 'Entsperren',
  'unlock.opening': 'Wird geöffnet…',
  'unlock.withFingerprint': 'Mit Fingerabdruck entsperren',
  'unlock.wait': 'Noch {seconds}s warten',
  'unlock.tooManyAttempts': 'Zu viele Versuche. Versuchen Sie es in {seconds}s erneut.',
  'unlock.tooMany': 'Zu viele falsche PINs',
  'unlock.tooManyBackup': 'Nehmen Sie sich einen Moment. Sie können es unten weiter versuchen oder Ihren Zugang über Ihre Sicherungsanmeldung oder einen vertrauenswürdigen Kontakt wiederherstellen.',
  'unlock.tooManyLocal': 'Nehmen Sie sich einen Moment und versuchen Sie es sorgfältig erneut. Diese Wallet liegt nur auf diesem Gerät, es gibt also keine Sicherung.',
  'unlock.forgotPin': 'Safe-Wallet-PIN vergessen?',
  'unlock.errFailed': 'Diese Wallet konnte nicht geöffnet werden.',

  'chooser.chooseWallet': 'Wallet auswählen',
  'chooser.eachHasPin': 'Jede gespeicherte Wallet hat in diesem Browser ihre eigene PIN.',
  'chooser.current': 'Aktuell',
  'chooser.addAnother': 'Weiteres Konto hinzufügen',

  'existing.title': 'Vorhandene Wallet',
  'existing.heading': 'Meine Wallet öffnen',
  'existing.intro': 'Verwenden Sie Ihr Kontopasswort und Ihre Safe-Wallet-PIN, um diese Wallet auf diesem Gerät zu öffnen.',
  'existing.openWallet': 'Wallet öffnen',
  'existing.forgotPin': 'Safe-Wallet-PIN vergessen?',
  'existing.advanced': 'Erweitert: mit einem anderen Gerät freigeben',
  'existing.advancedNote': 'Nur für ältere Beta-Wallets, die noch nicht für die Anmeldung mit Passwort und PIN aktualisiert wurden.',
  'existing.requesting': 'Anfrage läuft…',
  'existing.requestApproval': 'Gerätefreigabe anfordern',
  'existing.approvalTitle': 'Gerätefreigabe',
  'existing.unavailable': 'Nicht verfügbar',
  'existing.calculating': 'Wird berechnet…',
  'existing.mismatchNote': 'Eine Abweichung bedeutet, dass der Kopplungsschlüssel unterwegs verändert worden sein könnte. Brechen Sie die Anfrage ab und geben Sie sie nicht frei.',
  'existing.cancelRequest': 'Anfrage abbrechen',
  'existing.approved': 'Gerät freigegeben',
  'existing.setPin': 'App-PIN festlegen',
  'existing.setPinNote': 'Diese PIN schützt den freigegebenen Wallet-Schlüssel auf diesem Gerät. Sie können dieselbe Safe-Wallet-PIN wie auf Ihren anderen persönlichen Geräten verwenden.',
  'existing.finishing': 'Wird abgeschlossen…',
  'existing.errPin': 'Verwenden Sie eine Safe-Wallet-PIN mit 6 bis 12 Ziffern.',
  'existing.errNoWallet': 'Dieses Konto hat noch keine Safe Wallet. Erstellen Sie stattdessen eine neue Wallet.',
  'existing.errUpgrade': 'Eine einmalige Aktualisierung ist nötig. Öffnen Sie diese Wallet einmal auf einem Gerät, auf dem sie bereits funktioniert, schalten Sie dort die Synchronisierung ein und versuchen Sie es dann erneut.',
  'existing.errRequest': 'Die Gerätefreigabe konnte nicht angefordert werden.',
  'existing.errCancel': 'Die Freigabeanfrage konnte nicht abgebrochen werden; sie läuft automatisch ab.',
  'existing.errFinish': 'Die Gerätefreigabe konnte nicht abgeschlossen werden.',
  'existing.errPairingInvalid': 'Der lokale Kopplungsschlüssel ist ungültig. Brechen Sie ab und starten Sie eine neue Anfrage.',
  'existing.errCancelled': 'Diese Freigabeanfrage wurde abgebrochen. Starten Sie eine neue Anfrage.',
  'existing.errExpired': 'Diese Freigabeanfrage ist abgelaufen. Starten Sie eine neue Anfrage.',

  'status.onDeviceOnly': 'Nur auf diesem Gerät',
  'status.syncing': 'Wird synchronisiert…',
  'status.synced': 'Synchronisiert',
  'status.offline': 'Offline',
  'status.saving': 'Wird gespeichert…',
  'status.savingLocal': 'Wird auf diesem Gerät gespeichert…',
  'status.openingDatabase': 'Lokale Datenbank wird geöffnet…',
  'status.couldNotStart': 'Start nicht möglich',
  'status.errInit': 'Safe Wallet konnte nicht gestartet werden.',
  'status.errDatabase': 'Die lokale Datenbank ist nicht bereit.',
  'menu.closeMenu': 'Menü schließen',
  'action.showAll': 'Alle anzeigen',

  'conflict.collection': 'Sammlung',
  'conflict.deletedVersion': 'Eine gelöschte Version steht im Widerspruch zu einer anderen Änderung',
  'conflict.multipleChanges': 'Mehrere Offline-Änderungen',
  'conflict.title': 'Konflikt',
  'conflict.chooseVersion': 'Wählen Sie die Version, die behalten werden soll',
  'conflict.note': 'Beide verschlüsselten Versionen bleiben erhalten, bis Sie wählen. Eine gelöschte Version kann bewusst ausgewählt werden.',
  'conflict.loading': 'Versionen werden geladen…',
  'conflict.deleted': 'Gelöschte Version',
  'conflict.resolving': 'Wird aufgelöst…',
  'conflict.keepDeleted': 'Gelöscht behalten',
  'conflict.useThis': 'Diese Version verwenden',
  'conflict.errOpen': 'Die widersprüchlichen Versionen konnten nicht geöffnet werden.',
  'conflict.errResolve': 'Dieser Konflikt konnte nicht aufgelöst werden.',
  'conflict.screenNote': 'Offline auf mehreren Geräten vorgenommene Änderungen bleiben verschlüsselt erhalten, bis Sie wählen, welche Version behalten werden soll.',
  'conflict.none': 'Keine Konflikte.',

  'collections.newName': 'Name der neuen Sammlung',
  'collections.note': 'Sammlungen sind private Ordner. Ein Eintrag kann zu mehreren Sammlungen gehören.',
  'collections.confirmDelete': 'Die Sammlung „{name}“ löschen? Die Einträge bleiben in Safe Wallet erhalten.',

  'item.confirmDelete': '„{title}“ löschen? Der Eintrag kann für begrenzte Zeit aus der Sicherungshistorie wiederhergestellt werden.',
  'reminder.confirmDone': 'Diese Erinnerung als erledigt markieren?\n\nDas Datum der Erinnerung bleibt am Eintrag erhalten.',
  'reset.confirmOther': '„{name}“ aus diesem Browser entfernen? Die verschlüsselte Sicherung im Konto wird nicht gelöscht, und Ihre anderen Wallets in diesem Browser bleiben unberührt. Sie benötigen das Sicherungskonto und die PIN, um sie hier wieder hinzuzufügen.',
  'reset.confirmLast': 'Diese lokale Wallet aus diesem Browser entfernen? Bereits synchronisierte verschlüsselte Daten werden nicht aus dem Konto gelöscht. Noch nicht hochgeladene Änderungen und die Gerätefreigabe dieses Browsers werden entfernt.',

  'merge.confirmAdd': 'Dieses Konto hat bereits eine Wallet. Möchten Sie Ihre {items} samt Fotos und Dateien dorthin übernehmen und zu dieser Wallet wechseln?\n\nIhre aktuelle Wallet bleibt in diesem Browser erhalten, bis Sie sie entfernen.',
  'merge.notice': '{items} samt Fotos und Dateien wurden zu dieser Wallet hinzugefügt. Ihre alte Wallet „{name}“ ist weiterhin in diesem Browser. Sobald ihre Sicherung hochgeladen ist, können Sie sie unten entfernen.',
  'merge.removeOld': 'Alte Wallet entfernen',
  'merge.backingUp': 'Sicherung läuft…',
  'merge.dismiss': 'Ausblenden',
  'merge.confirmRemoveOld': 'Die alte Wallet „{name}“ aus diesem Browser entfernen?\n\nIhre Einträge wurden bereits in diese Wallet kopiert und in Ihrem Konto gesichert. Damit wird nur die alte lokale Kopie in diesem Browser gelöscht.',
  'merge.errMismatch': 'Die geöffnete Wallet passt nicht zum Konto, das für die Übernahme ausgewählt wurde. Aus der ursprünglichen Wallet wurde nichts entfernt.',
  'merge.errOpenLocal': 'Öffnen Sie die lokale Wallet, bevor Sie ihre Einträge in eine andere Wallet übernehmen.',
  'merge.errNoWallet': 'Dieses Konto hat keine Wallet, zu der Einträge hinzugefügt werden können.',
  'merge.errKeyPackage': 'Öffnen Sie die Wallet des Kontos einmal auf dem Gerät, auf dem sie bereits funktioniert, und versuchen Sie es dann erneut.',
  'merge.errPin': 'Diese PIN öffnet die bereits in diesem Konto vorhandene Wallet nicht. Geben Sie die PIN dieser Wallet ein.',

  'pin.errNoSync': 'Die Synchronisierung ist nicht eingerichtet, daher kann die PIN für neue Geräte nicht aktualisiert werden.',
  'pin.errNoEmail': 'Für diese Wallet ist auf diesem Gerät keine Konto-E-Mail hinterlegt.',
  'pin.errOffline': 'Sie scheinen offline zu sein. Stellen Sie die Verbindung wieder her, um die PIN für die Anmeldung auf neuen Geräten zu ändern.',
  'pin.errWrongPassword': 'Dieses Kontopasswort ist nicht korrekt.',
  'pin.errDeviceUnavailable': 'Dieses Gerät ist nicht verfügbar.',
  'pin.changedBoth': 'PIN auf diesem Gerät und für die Anmeldung auf neuen Geräten geändert.',
  'pin.changedLocal': 'App-PIN geändert. Verwenden Sie die neue PIN, wenn dieses Gerät das nächste Mal gesperrt wird.',

  'recover.accountTitle': 'Safe-Wallet-Konto',
  'recover.passwordChanged': 'Passwort geändert',
  'recover.passwordChangedNote': 'Melden Sie sich mit dem neuen Kontopasswort erneut an. Ihre App-PIN und Ihre verschlüsselte Wallet wurden nicht verändert.',
  'recover.continue': 'Weiter',
  'recover.choosePassword': 'Neues Passwort wählen',
  'recover.choosePasswordNote': 'Damit wird nur das Passwort des Synchronisierungskontos geändert. Ihre App-PIN bleibt unverändert, und allein damit lässt sich Ihre verschlüsselte Wallet auf einem neuen Gerät nicht öffnen.',
  'recover.checkingLink': 'Wiederherstellungslink wird geprüft…',
  'recover.newPassword': 'Neues Kontopasswort',
  'recover.repeatPassword': 'Neues Passwort wiederholen',
  'recover.updating': 'Wird aktualisiert…',
  'recover.updatePassword': 'Passwort aktualisieren',
  'recover.errShort': 'Verwenden Sie mindestens 8 Zeichen.',
  'recover.errMismatch': 'Die beiden Passworteingaben stimmen nicht überein.',
  'recover.errUpdate': 'Das Kontopasswort konnte nicht aktualisiert werden.',

  'recover.walletTitle': 'Wallet wiederherstellen',
  'recover.freshVerification': 'Für die Wiederherstellung einer Wallet ist immer eine erneute Prüfung Ihres Kontos nötig. Safe Wallet fragt einen vertrauenswürdigen Kontakt niemals nach Ihrem Code.',
  'recover.useOnlyWhen': 'Verwenden Sie dies nur, wenn kein eigenes Gerät Ihre PIN zurücksetzen kann. Sie benötigen eine erneute E-Mail-Prüfung und die Zustimmung eines vertrauenswürdigen Safe-Wallet-Kontakts.',
  'recover.accountEmail': 'Konto-E-Mail',
  'recover.emailCode': 'Bestätigungscode aus der E-Mail',
  'recover.sendAnother': 'Neuen Code senden',
  'recover.authenticator': 'Authenticator',
  'recover.authenticatorCode': 'Authenticator-Code',
  'recover.verifyStart': 'E-Mail prüfen und starten',
  'recover.verifyContinue': 'E-Mail prüfen und fortfahren',
  'recover.deviceFingerprint': 'Fingerabdruck des wiederherstellenden Geräts',
  'recover.readFingerprint': 'Lesen Sie diesen Fingerabdruck Ihrem vertrauenswürdigen Kontakt in einem laufenden Gespräch vor. Er sollte Sie über Kontaktdaten anrufen, die er bereits hatte — niemals über eine Nummer, die ihm dafür zugeschickt wurde.',
  'recover.status': 'Status:',
  'recover.waitingEnds': 'Wartezeit endet:',
  'recover.expires': 'Läuft ab:',
  'recover.cancel': 'Wiederherstellung abbrechen',
  'recover.setNewPin': 'Neue App-PIN festlegen',
  'recover.keyRebuilt': 'Ihr Wallet-Schlüssel wurde nur in diesem Browser wiederhergestellt. Der Wiederherstellungsschutz ist damit aufgebraucht und muss neu eingerichtet werden.',
  'recover.deviceName': 'Gerätename',
  'recover.newPin': 'Neue App-PIN',
  'recover.repeatPin': 'PIN wiederholen',

  'invite.securityConfirmation': 'Sicherheitsbestätigung',
  'invite.title': 'Einladung zur Wiederherstellung',
  'invite.helpSomeone': 'Jemandem helfen, dem Sie vertrauen',
  'invite.invitedBy': 'Eingeladen von',
  'invite.askedYou': 'Diese Person hat Sie gebeten, ihr vertrauenswürdiger Wiederherstellungskontakt zu sein.',

  'invite.cannotSee': 'Sie können ihre Wallet nicht sehen.',
  'invite.cannotSeeNote': 'Was dort gespeichert ist, bleibt verschlüsselt und privat.',
  'invite.cannotRecover': 'Sie können sie nicht allein wiederherstellen.',
  'invite.cannotRecoverNote': 'Sie helfen erst, nachdem die Person die Wiederherstellung selbst gestartet hat.',
  'invite.nothingToRemember': 'Sie müssen sich nichts merken.',
  'invite.nothingToRememberNote': 'Safe Wallet bereitet alles sicher vor, sobald Sie zustimmen.',
  'invite.enterPin': 'PIN eingeben',
  'invite.confirming': 'Wird bestätigt…',
  'invite.decline': 'Ablehnen',
  'invite.accept': 'Einladung annehmen',
  'invite.confirmWithPin': 'Mit Ihrer PIN bestätigen',
  'invite.acceptTitle': 'Einladung zur Wiederherstellung annehmen',
  'invite.confirmBecome': 'Bestätigen Sie, dass Sie Wiederherstellungskontakt werden möchten für:',
  'invite.declineTitle': 'Einladung ablehnen',
  'invite.declineQuestion': 'Nicht Wiederherstellungskontakt werden?',
  'invite.invitationFrom': 'Die Einladung von',
  'invite.keep': 'Einladung behalten',
  'invite.accepted': 'Einladung angenommen',
  'invite.nowContact': 'Sie sind jetzt Wiederherstellungskontakt',
  'invite.nothingUnless': 'Sie müssen nichts tun, außer wenn',
  'invite.inboxTitle': 'Einladungen zur Wiederherstellung',
  'invite.review': 'Einladung prüfen',
  'invite.errConfirm': 'Das konnte nicht bestätigt werden.',
  'invite.errAccept': 'Diese Einladung konnte nicht angenommen werden.',
  'invite.errDecline': 'Diese Einladung konnte nicht abgelehnt werden.',

  'protect.preparing': 'Wiederherstellungsschutz wird vorbereitet…',
  'protect.addPerson': 'Vertrauensperson hinzufügen',
  'protect.addPersonNote': 'Eine Vertrauensperson kann Ihnen helfen, wieder in Ihre Wallet zu kommen. Sie kann Ihre Wallet nicht sehen und sie nicht ohne Sie wiederherstellen.',
  'protect.addSoYouCan': 'Fügen Sie eine Vertrauensperson hinzu, damit Sie wieder hineinkommen, wenn Sie Ihre PIN oder Ihr Gerät verlieren.',
  'protect.mustHaveAccount': 'Diese Person muss bereits ein Safe-Wallet-Konto haben.',
  'protect.useTheirEmail': 'Verwenden Sie die E-Mail-Adresse, die mit ihrer Safe Wallet verbunden ist.',
  'protect.emailLabel': 'Safe-Wallet-E-Mail der Vertrauensperson',
  'protect.emailPlaceholder': 'name@beispiel.de',
  'protect.send': 'Einladung senden',
  'protect.yourPeople': 'Ihre Vertrauenspersonen',
  'protect.onlyYouSee': 'Nur Sie sehen diese Liste.',
  'protect.waiting': 'Warten auf Zustimmung',
  'protect.pending': 'Ausstehend',
  'protect.canHelp': 'Kann helfen, wenn Sie eine Wiederherstellung anfordern',
  'protect.active': 'Aktiv',
  'protect.protectionActive': 'Wiederherstellungsschutz ist aktiv',
  'protect.becomesActive': 'Der Wiederherstellungsschutz wird wirksam, sobald eine eingeladene Person zustimmt.',
  'protect.errLoad': 'Der Wiederherstellungsschutz konnte nicht geladen werden.',
  'protect.errSelf': 'Wählen Sie eine andere Safe-Wallet-Nutzerin oder einen anderen Safe-Wallet-Nutzer.',
  'protect.errNoAccount': 'Zu dieser E-Mail-Adresse wurde kein Safe-Wallet-Konto gefunden. Bitten Sie die Person, zuerst ein Konto zu erstellen und zu bestätigen.',
  'protect.errNotReady': 'Dieses Safe-Wallet-Konto ist noch nicht bereit für Einladungen zur Wiederherstellung. Bitten Sie die Person, ihre aktualisierte Safe Wallet einmal zu öffnen und zu entsperren, und versuchen Sie es dann erneut.',
  'protect.errCheck': 'Dieses Safe-Wallet-Konto konnte nicht geprüft werden.',
  'protect.errSend': 'Diese Einladung konnte nicht gesendet werden.',
  'invite.willBeRemoved': 'wird entfernt. Sie kann Sie später erneut einladen.',
  'invite.asksForHelp': 'um Hilfe bittet, wieder in ihre Wallet zu kommen.',
  'protect.notSetUp': 'Nicht eingerichtet',
  'protect.checking': 'Wird geprüft…',
  'reminder.done': 'Erledigt',
  'reminder.doneOn': 'Erledigt am {date}',
  'invite.enterWalletPin': 'Safe-Wallet-PIN eingeben',
  'invite.confirmInviteNote': 'Bestätigen Sie, dass Sie diese Person als Wiederherstellungskontakt einladen möchten.',
  'invite.confirmInvite': 'Einladung bestätigen',
  'recover.startTitle': 'Wiederherstellung starten',
  'recover.continueTitle': 'Wiederherstellung fortsetzen',
  'invite.invitedYouTo': 'hat Sie als vertrauenswürdigen Wiederherstellungskontakt eingeladen.',
  'approve.inboxTitle': 'Jemand braucht Ihre Hilfe',
  'approve.isRecovering': 'versucht, wieder in die eigene Wallet zu kommen.',
  'approve.review': 'Anfrage prüfen',
  'approve.title': 'Zugang wieder freigeben?',
  'approve.fingerprintLabel': 'Code auf dem neuen Gerät der Person',
  'approve.callFirst': 'Rufen Sie die Person zuerst an — unter einer Nummer, die Sie bereits hatten, niemals unter einer, die Ihnen zugeschickt wurde. Lassen Sie sich den Code von ihrem Bildschirm vorlesen und prüfen Sie, ob er genau mit diesem übereinstimmt.',
  'approve.onlyIfSure': 'Geben Sie nur frei, wenn der Code übereinstimmt und Sie sicher sind, dass es wirklich diese Person ist. Wenn nicht, sagen Sie ihr sofort Bescheid, damit sie die Anfrage abbrechen kann.',
  'approve.approve': 'Codes stimmen überein — freigeben',
  'approve.approving': 'Wird freigegeben…',
  'approve.notNow': 'Jetzt nicht',
  'approve.done': 'Freigegeben. Die Person kann jetzt wieder hinein.',
  'approve.errApprove': 'Diese Anfrage konnte nicht freigegeben werden.',
  'approve.waitUntil': 'Fortsetzen ist ab {time} möglich.',
};

export const plurals: Plurals = {
  items: { one: '{count} Eintrag', other: '{count} Einträge' },
  minutes: { one: '{count} Minute', other: '{count} Minuten' },
  queued: { one: '{count} ausstehende Änderung', other: '{count} ausstehende Änderungen' },
  helpers: { one: '{count} Vertrauensperson kann helfen.', other: '{count} Vertrauenspersonen können helfen.' },
};
